import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  NoSsr,
  Grid,
  makeStyles,
  Typography,
  Chip,
  Hidden,
  SwipeableDrawer,
  FormControlLabel,
  Switch,
  Tooltip
} from '@material-ui/core'
import { ArrowBack, GitHub, LinkedIn } from '@material-ui/icons'
import React, { Fragment, useMemo, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import i18n from 'i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Trans } from 'react-i18next'
import { HelpOutlineRounded } from '@material-ui/icons'
import { useFetch, mapByIds, useAuth } from '../../components/context/useFetch'
import Layout from '../../components/layout'
import { pictureUrl } from '../../components/common/pictureUrl'
import { CandidatesFiltering, Choices, useCandidatesFilters } from '../../components/dashboard/Filtering'
import APPLICATION_STATUSES, { useStatusesStyles } from '../../components/application_status'
import { useUpdate } from '../../components/reducer'
import allPagesProps from '../../components/common/all_pages_props'

const PER_PAGE = 50

const Page = () => (
  <NoSsr>
    <PageLayout />
  </NoSsr>
)

export default Page

export const getServerSideProps = async (context) => ({ props: await allPagesProps(context) })

const getStoredCandidatesFilters = () =>
  JSON.parse(localStorage.getItem('candidate-filters-2') || '{}')

const normalizeApplicationsResult = (result) => ({
  ...result,
  orderedData: result.data,
  data: mapByIds(result.data)
})

const buildApplicationsUrl = (page, filters = []) => {
  const params = new URLSearchParams({
    version: '3',
    page: page.toString(),
    per_page: PER_PAGE.toString()
  })

  if (filters.length > 0) {
    params.set('filters', filters.join('|'))
  }

  return `profile/applications?${params.toString()}`
}

const PageLayout = () => {
  const candidateFilters = useMemo(() => getStoredCandidatesFilters(), []);

  return (
    <Layout pageTitle="pages_titles.application" initialData={{ candidateFilters }}>
      <Container maxWidth="md">
        <CandidatesFiltering />
      </Container>
      <MyCandidates />
    </Layout>
  )
}

const MyCandidates = () => useFetch(
  ({ fetch }) => ({
    applications: fetch(buildApplicationsUrl(1)).then((result) => normalizeApplicationsResult(result)),
    jobs: fetch('jobs')
  }),
  ({ applications }) => {
    const router = useRouter()
    const profile = useSelector((state) => state.profile)
    const classes = useStatusesStyles()
    const auth = useAuth()
    const update = useUpdate()
    const [allApplications, setAllApplications] = useState(applications)
    const [orderedApplications, setOrderedApplications] = useState(applications.orderedData || Object.values(applications.data || {}))
    const [currentPage, setCurrentPage] = useState(applications.meta?.current_page || 1)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const scrollPositionRef = useRef(null)
    const didSkipFirstDefaultFetch = useRef(false)

    const candidateFilters = useSelector((state) => state.candidateFilters || {})
    const activeFilterIds = (candidateFilters.all || []).filter((idWithType) => idWithType !== 'Pool_false')
    const activeFiltersKey = activeFilterIds.join('|')

    useEffect(() => {
      setAllApplications(applications)
      setOrderedApplications(applications.orderedData || Object.values(applications.data || {}))
      setCurrentPage(applications.meta?.current_page || 1)
      update(['applications'], applications)
    }, [applications])

    useEffect(() => {
      if (scrollPositionRef.current === null) return

      window.scrollTo(0, scrollPositionRef.current)
      scrollPositionRef.current = null
    }, [orderedApplications])

    useEffect(() => {
      if (!didSkipFirstDefaultFetch.current) {
        didSkipFirstDefaultFetch.current = true
        if (activeFilterIds.length === 0) return
      }

      setIsLoadingMore(true)
      auth.fetch(buildApplicationsUrl(1, activeFilterIds)).then((result) => {
        const normalized = normalizeApplicationsResult(result)

        setAllApplications(normalized)
        setOrderedApplications(normalized.orderedData || [])
        setCurrentPage(normalized.meta?.current_page || 1)
      }).finally(() => setIsLoadingMore(false))
    }, [activeFiltersKey])

    const filterVivier = (e) => {
      if (e.target.checked) update(['candidateFilters', 'all'], ['Pool_true'])
      else update(['candidateFilters', 'all'], ['Pool_false'])
    }

    const handleShowMore = () => {
      const totalPages = allApplications.meta?.total_pages || 1
      if (isLoadingMore || currentPage >= totalPages) return

      const nextPage = currentPage + 1
      setIsLoadingMore(true)
      scrollPositionRef.current = window.scrollY

      auth.fetch(buildApplicationsUrl(nextPage, activeFilterIds)).then((result) => {
        setOrderedApplications((previous) => [...previous, ...result.data])

        setAllApplications((previous) => {
          const mergedIncluded = { ...previous.included }
          if (result.included) {
            Object.entries(result.included).forEach(([type, items]) => {
              mergedIncluded[type] = { ...(mergedIncluded[type] || {}), ...items }
            })
          }

          const merged = {
            ...previous,
            ...result,
            orderedData: [...(previous.orderedData || []), ...result.data],
            data: {
              ...previous.data,
              ...mapByIds(result.data)
            },
            included: mergedIncluded
          }
          return merged
        })

        setCurrentPage(result.meta?.current_page || nextPage)
      }).finally(() => setIsLoadingMore(false))
    }

    const displayedApplications = orderedApplications
    const totalCandidatesCount = allApplications.meta?.total_count || displayedApplications.length

    if (!profile) return null

    return (
      <Container>
        <Box my={4}>
          <FilterPanel filters={candidateFilters} />
        </Box>
        <Grid container alignItems="center">
          <Grid item md={10} xs={8}>
            <Typography variant="h1">{profile.name}</Typography>
          </Grid>
          <Grid item md={2} xs={4}>
            <FormControlLabel
              control={<Switch color="primary" />}
              label={i18n.t('main.filtering.vivier')}
              labelPlacement="start"
              onChange={filterVivier}
              checked={candidateFilters.all?.includes('Pool_true')}
            />
            <Tooltip title={i18n.t('main.filtering.tooltip_vivier')}>
              <HelpOutlineRounded style={{ verticalAlign: 'middle', margin: '2%' }} />
            </Tooltip>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h2" align="center">
              {candidateFilters.all?.includes('Pool_true') ? i18n.t('candidature.title_vivier') : i18n.t('candidature.title')}({totalCandidatesCount})
            </Typography>
            {displayedApplications.length === 0 && (
              <Box p={4}>
                <Typography align="center">
                  <img src="/portfolio.png" style={{ maxWidth: '500px' }} />
                </Typography>

                <Typography align="center">
                  {i18n.t('mycandidates.no_candidate.1')} <br />
                  {i18n.t('mycandidates.no_candidate.2')}
                </Typography>

                {auth.can('new', 'job_offer') && (
                  <Typography align="center">
                    <Link href="/job-offer/new">
                      <Button variant="outlined" color="secondary">
                        {i18n.t('mycandidates.no_candidate.btn')}
                      </Button>
                    </Link>
                  </Typography>
                )}
              </Box>
            )}
            <Grid container spacing="2">
              {displayedApplications.map((application) => (
                <Grid item xs={12} sm={4} md={3} style={{ position: 'relative' }} key={application.candidate_id}>
                  <Card>
                    <CardHeader
                      style={{ marginTop: '20px' }}
                      avatar={<ProfilePic candidateId={application.candidate_id} />}
                      title={
                        <Typography variant="h6">
                          <Link href={`/entreprise/profile/application/${application.hashid}`}>
                            <a>{`${application.first_name} ${application.last_name}`}</a>
                          </Link>
                        </Typography>
                      }
                      subheader={application.title}
                    />
                    <CardContent height="500px">
                      <Grid container>
                        <Grid item xs={12}>
                          <Box borderRadius="5px" width="100%" mr={0} p={1} style={{ height: '79px' }}>
                            <Typography variant="caption" style={{ fontSize: '12px' }}>
                              {!application.description_excerpt && <em>{i18n.t('mycandidates.no_description')}</em>}
                              {application.description_excerpt &&
                                (application.description_excerpt.length > 100 ? `${application.description_excerpt.slice(0, 100)}...` : application.description_excerpt)}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography align="center" style={{ marginTop: 10 }}>
                            {application.email}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography align="center" style={{ fontSize: '14px', marginTop: 10 }}>
                            {application.phone}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Chip
                        label={APPLICATION_STATUSES[application.status].label}
                        icon={APPLICATION_STATUSES[application.status].icon}
                        className={classes[`${application.status}Chip`]}
                      />
                    </CardContent>
                    <CardActions>
                      <Button
                        onClick={() => router.push(`/entreprise/profile/application/${application.hashid}`)}
                        color="secondary"
                        variant="outlined"
                        style={{
                          fontSize: '10px',
                          marginTop: 5,
                          marginRight: 5,
                          width: '100%'
                        }}>
                        {i18n.t('candidature.newest')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {displayedApplications.length > 0 && (
              <Box mt={4} textAlign="center">
                <Typography variant="body1" gutterBottom>
                  {i18n.t('mycandidates.showing_count', {
                    count: displayedApplications.length,
                    total: allApplications.meta?.total_count || displayedApplications.length
                  })}
                </Typography>
                {currentPage < (allApplications.meta?.total_pages || 1) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleShowMore}
                    style={{ marginTop: '16px' }}
                    disabled={isLoadingMore}>
                    {i18n.t('mycandidates.show_more')}
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    )
  }
)

const useStyles = makeStyles((theme) => ({
  mediumImg: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    margin: '0 auto',
    cursor: 'pointer'
  }
}))

export const ProfilePic = ({ picture, candidateId }) => {
  let pic
  if (picture) {
    pic = {
      record_type: 'Candidate',
      record_id: candidateId,
      id: picture.id,
      name: 'picture',
      created_at: picture.created_at
    }
  } else {
    pic = {
      record_type: 'Candidate',
      record_id: candidateId,
      name: 'picture'
    }
  }

  return (
    <label>
      <Avatar className={useStyles().mediumImg} src={pictureUrl(pic, 200, 200)} />
    </label>
  )
}

const FilterPanel = ({ filters }) => (
  <div>
    <Hidden smUp>
      <SwipeableDrawer open={filters.expand} onClose={() => update(['filters', 'expand'], false)} style={{ width: '80vw' }}>
        <Box p={3}>
          <Typography variant="h2">
            <Trans i18nKey="main.filtering.filter" />
          </Typography>

          <DrawerContent />
        </Box>
      </SwipeableDrawer>
    </Hidden>

    {filters.expand && (
      <Hidden xs>
        <Box mb={3}>
          <Card elevation={1} style={{ backgroundColor: 'inherit' }}>
            <CardContent>
              <Grid container direction="row">
                <DrawerContent />
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Hidden>
    )}
  </div>
)

const DrawerContent = () => {
  const filters = useCandidatesFilters()

  return (
    <Fragment>
      <Grid item xs={12} sm={2}>
        <Typography variant="h4">
          1. <Trans i18nKey="mycandidates.filters.filter_panel.statuses" />
        </Typography>

        <Choices choices={filters.statuses} filterKey="candidateFilters" showAll />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Typography variant="h4">
          2. <Trans i18nKey="mycandidates.filters.filter_panel.job_offer" />
        </Typography>

        <Choices choices={filters.offers} filterKey="candidateFilters" showAll />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Typography variant="h4">
          3. <Trans i18nKey="mycandidates.filters.filter_panel.job" />
        </Typography>

        <Choices choices={filters.jobs} filterKey="candidateFilters" showAll />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Typography variant="h4">
          4. <Trans i18nKey="mycandidates.filters.filter_panel.technologies" />
        </Typography>

        <Choices choices={filters.technologies} filterKey="candidateFilters" showAll />
      </Grid>
    </Fragment>
  )
}
